import { toast } from "react-toastify";

// Utility function to find the most frequent value in an array
export function mostFrequent(arr) {
  if (!arr.length) return null;
  const counts = arr.reduce((acc, v) => {
    acc[v] = (acc[v] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts).reduce(
    (a, b) => (b[1] > a[1] ? b : a),
    [null, 0]
  )[0];
}

// Check if video format
export function isVideoFormat(imageUrl) {
  const videoFormats = ["mp4", "avi", "mov", "mkv"];
  return videoFormats.some((ext) => imageUrl?.toLowerCase().includes(ext));
}

// Get video duration
export function getVideoDuration(imageUrl, onSuccess, onError) {
  const video = document.createElement("video");
  video.src = imageUrl;
  video.preload = "metadata";
  video.crossOrigin = "anonymous";


  video.onloadedmetadata = () => {
    onSuccess(Math.ceil(video.duration));
  };

  video.onerror = () => {
    onError();
  };
}

// Handle adding items to playlist with orientation checking
export function handleAddItemsToPlaylist(
  selectedItems,
  dataToDisplay,
  items,
  setItems,
  setPlaylistOrientation
) {
  const newItems = [];

  selectedItems.forEach((customizationId) => {
    const matched = dataToDisplay?.find((i) => i.id === customizationId);
    if (!matched) return;

    const orientation =
      matched.customProperties?.metaData?.orientation || "landscape";
    const imageUrl = matched.customisations?.[0]?.imageUrl;
    const isVideo = isVideoFormat(imageUrl);

    newItems.push({ customizationId, orientation, imageUrl, isVideo });
  });

  newItems.forEach(({ customizationId, orientation, imageUrl, isVideo }) => {
    if (!items.some((i) => i.customizationId === customizationId)) {
      if (isVideo) {
        getVideoDuration(
          imageUrl,
          (duration) => {
            setItems((prev) => [
              ...prev,
              {
                customizationId,
                orientation,
                duration,
              },
            ]);
          },
          () => {
            setItems((prev) => [
              ...prev,
              { customizationId, orientation, duration: 10 },
            ]);
          }
        );
      } else {
        setItems((prev) => [
          ...prev,
          { customizationId, orientation, duration: 10 },
        ]);
      }
    }
  });

  const allOrientations = [
    ...items.map((i) => i.orientation),
    ...newItems.map((i) => i.orientation),
  ];

  if (allOrientations?.length === 0) return;

  const modeOrientation = mostFrequent(allOrientations);
  setPlaylistOrientation(modeOrientation);
}

// Handle duration change for a specific item
export function handleDurationChange(index, value, items, setItems) {
  const newItems = [...items];
  newItems[index] = { ...newItems[index], duration: parseInt(value) || 0 };
  setItems(newItems);
}

// Handle removing an item from playlist
export function handleRemoveItem(
  index,
  items,
  setItems,
  currentPreviewIndex,
  setCurrentPreviewIndex
) {
  setItems(items.filter((_, i) => i !== index));
  if (currentPreviewIndex >= items.length - 1) {
    setCurrentPreviewIndex(Math.max(0, currentPreviewIndex - 1));
  }
}

// Handle moving item up in playlist
export function handleMoveUp(index, items, setItems) {
  if (index === 0) return;
  const newItems = [...items];
  [newItems[index], newItems[index - 1]] = [
    newItems[index - 1],
    newItems[index],
  ];
  setItems(newItems);
}

// Handle moving item down in playlist
export function handleMoveDown(index, items, setItems) {
  if (index === items.length - 1) return;
  const newItems = [...items];
  [newItems[index], newItems[index + 1]] = [
    newItems[index + 1],
    newItems[index],
  ];
  setItems(newItems);
}

// Handle setting duration for all items
export function handleSetDurationForAll(duration, items, setItems, setOpen) {
  const parsedDuration = parseInt(duration, 10);
  if (!isNaN(parsedDuration) && parsedDuration > 0) {
    setItems((prevItems) =>
      prevItems.map((item) => ({
        ...item,
        duration: parsedDuration,
      }))
    );
    setOpen(false);
  } else {
    alert("Please enter a valid positive number for the duration.");
  }
}

// Slideshow controls
export function startSlideshow(
  items,
  setCurrentPreviewIndex,
  setLoading,
  setIsSlideshowActive,
  setSlideshowIntervalId
) {
  setLoading(true);
  if (items?.length > 0) {
    setCurrentPreviewIndex(0);
    const intervalId = setInterval(() => {
      setCurrentPreviewIndex((prevIndex) => {
        if (prevIndex < items?.length - 1) {
          return prevIndex + 1;
        } else {
          clearInterval(intervalId);
          setLoading(false);
          return prevIndex;
        }
      });
    }, 3000);
    setSlideshowIntervalId(intervalId);
    setIsSlideshowActive(true);
  }
}

export function stopSlideshow(
  isSlideshowActive,
  slideshowIntervalId,
  setSlideshowIntervalId,
  setIsSlideshowActive
) {
  if (isSlideshowActive) {
    clearInterval(slideshowIntervalId);
    setSlideshowIntervalId(null);
    setIsSlideshowActive(false);
  }
}

// Toggle slideshow
export function toggleSlideshow(
  isPlaying,
  startSlideshow,
  stopSlideshow,
  setIsPlaying
) {
  if (isPlaying) {
    stopSlideshow();
  } else {
    startSlideshow();
  }
  setIsPlaying(!isPlaying);
}

// Navigation functions
export function goToPreviousPreview(
  currentPreviewIndex,
  setCurrentPreviewIndex,
  items
) {
  setCurrentPreviewIndex((prevIndex) =>
    prevIndex > 0 ? prevIndex - 1 : items.length - 1
  );
}

export function goToNextPreview(
  currentPreviewIndex,
  setCurrentPreviewIndex,
  items
) {
  setCurrentPreviewIndex((prevIndex) =>
    prevIndex < items.length - 1 ? prevIndex + 1 : 0
  );
}

// Check if customization is selected
export function isCustomizationSelected(customizationId, items) {
  return items?.some((item) => item.customizationId === customizationId);
}

// Transform playlist items for API
export function transformPlaylistItems(playlist) {
  return playlist.items.map((item, index) => {
    let type = "content";
    let subtype = null;

    if (item?.customProperties?.html) {
      type = "template";
      subtype = "html";
    } else if (item.id === "content-ai") {
      type = "contentAI";
    } else if (item.customisations) {
      type = "content";
      subtype = "image/video";
    }

    return {
      id: index + 1,
      type,
      subtype,
      content_ref: item.id,
      duration: item.duration,
      transition: "fade",
      customProperties: item.customProperties || {},
    };
  });
}

// Orientation mismatch handling
export function showMismatchError(mismatchToastId) {
  if (!toast.isActive(mismatchToastId.current)) {
    mismatchToastId.current = toast.error(
      "Error: Images have different orientations.",
      {
        position: toast.POSITION.TOP_CENTER,
        autoClose: false,
        closeOnClick: true,
        draggable: false,
      }
    );
  }
}

export function dismissMismatchError(mismatchToastId) {
  if (toast.isActive(mismatchToastId.current)) {
    toast.dismiss(mismatchToastId.current);
    mismatchToastId.current = null;
  }
}

// Check for orientation mismatch
export function checkOrientationMismatch(items, playlistOrientation) {
  return items.some((i) => i.orientation !== playlistOrientation);
}

// Get mismatched item IDs
export function getMismatchedIds(items, playlistOrientation) {
  return items
    .filter((item) => item.orientation !== playlistOrientation)
    .map((item) => item.customizationId);
}
